import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Warehouse, ChevronLeft, ChevronRight, X, DollarSign, Fence, Receipt, Truck, UserCog, Lock, BarChart3 } from 'lucide-react';
import { AviporkLogo } from '../AviporkLogo';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/', testId: 'nav-dashboard', moduleKey: 'dashboard' },
  { label: 'Lotes', icon: Fence, path: '/configuracion', testId: 'nav-configuracion', moduleKey: 'lotes' },
  { label: 'Producción Diaria', icon: ClipboardList, path: '/produccion', testId: 'nav-produccion-diaria', moduleKey: 'produccion' },
  { label: 'Análisis de Producción', icon: BarChart3, path: '/analisis', testId: 'nav-analisis', moduleKey: 'analisis' },
  { label: 'Almacén e Inventario', icon: Warehouse, path: '/almacen', testId: 'nav-almacen-inventario', moduleKey: 'almacen' },
  { label: 'Despacho', icon: Truck, path: '/despacho', testId: 'nav-despacho', moduleKey: 'despacho' },
  { label: 'Costos y ROI', icon: DollarSign, path: '/costos', testId: 'nav-costos', moduleKey: 'costos' },
  { label: 'Cuentas por Cobrar', icon: Receipt, path: '/cxc', testId: 'nav-cxc', moduleKey: 'cxc' },
  { label: 'Gestión de Usuarios', icon: UserCog, path: '/usuarios', testId: 'nav-usuarios', moduleKey: 'gestion_usuarios', superAdminOnly: true },
];

// Colores del sidebar (verde pastel del PDF de facturas)
const SIDEBAR_BG = '#C6E5B1';
const SIDEBAR_BORDER = 'rgba(0, 100, 0, 0.15)';

export const Sidebar = ({ open, onOpenChange, collapsed, onCollapsedChange }) => {
  const location = useLocation();
  const { hasModuleAccess, isSuperAdmin } = useAuth();

  // Filtramos los módulos super_admin si el usuario no lo es
  const visibleItems = navItems.filter((item) => !item.superAdminOnly || isSuperAdmin);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div
        className={cn(
          "flex items-center justify-center px-3 border-b py-3",
          collapsed ? "h-20" : "h-28"
        )}
        style={{ borderColor: SIDEBAR_BORDER }}
      >
        {collapsed ? (
          <AviporkLogo compact />
        ) : (
          <AviporkLogo />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.path;
          const hasAccess = hasModuleAccess(item.moduleKey);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              onClick={() => onOpenChange(false)}
              title={!hasAccess ? `Sin permiso: ${item.label}` : item.label}
              className={cn(
                "relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150",
                collapsed && "justify-center px-2",
                isActive
                  ? "bg-[#0A4D14] text-white shadow-sm"
                  : hasAccess
                    ? "text-[#0A4D14] hover:bg-[#0A4D14]/10"
                    : "text-[#0A4D14]/40 hover:bg-[#0A4D14]/5"
              )}
            >
              {isActive && !collapsed && (
                <div className="absolute left-0 w-1 h-6 bg-white rounded-r" />
              )}
              <item.icon className={cn("flex-shrink-0", collapsed ? "w-5 h-5" : "w-[18px] h-[18px]")} />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {!hasAccess && (
                    <Lock className="w-3 h-3 flex-shrink-0 opacity-60" data-testid={`lock-${item.testId}`} />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle (desktop only) */}
      <div className="hidden md:flex border-t p-3" style={{ borderColor: SIDEBAR_BORDER }}>
        <Button
          variant="ghost"
          size="sm"
          data-testid="sidebar-toggle"
          onClick={() => onCollapsedChange(!collapsed)}
          className="w-full text-[#0A4D14]/70 hover:text-[#0A4D14] hover:bg-[#0A4D14]/10"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          {!collapsed && <span className="ml-2 text-xs">Colapsar</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className={cn(
          "hidden md:flex flex-col flex-shrink-0 transition-all duration-200 border-r",
          collapsed ? "w-[76px]" : "w-[260px]"
        )}
        style={{ backgroundColor: SIDEBAR_BG, borderColor: SIDEBAR_BORDER }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile overlay sidebar */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => onOpenChange(false)}
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-[260px] md:hidden flex flex-col border-r"
            style={{ backgroundColor: SIDEBAR_BG, borderColor: SIDEBAR_BORDER }}
          >
            <div className="absolute right-2 top-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="text-[#0A4D14]/70 hover:text-[#0A4D14] hover:bg-[#0A4D14]/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
};

export default Sidebar;
